import React, { useState, useEffect, useRef } from "react";
import { TextField, Container, makeStyles, Grid } from "@material-ui/core/";
import { Autocomplete } from "@material-ui/lab";
import { useHistory } from "react-router-dom";
import * as S from "./styles";
import cleanStorageBeforeSearch from "../../../utils/cleanStorageBeforeSearch";
import { getSessionStorage } from "../../../utils/getSessionStorage";
import SearchResults from "./SearchResults";
import CivitatisExperienceRepository from "../../../repositories/civitatisExperienceRepository";
import filterFreeTours from "../../../utils/filterFreeTours";
import { remove } from "diacritics";

const useStyles = makeStyles(() => ({
	root: {
		'& .MuiFormHelperText-root.Mui-error': { color: 'white' },
		opacity: 1,
		'& .MuiInputBase-input': { color: '#FFF' },
		'& label.Mui-focused': { color: '#FFF' },
		'& .MuiInput-underline:after': { borderBottomColor: '#FFF', color: '#FFF' },
		'& .MuiFormLabel-root': { color: '#FFF', opacity: 0.45 },
		'& .MuiSelect-icon': { color: '#FFF' },
		'& .MuiOutlinedInput-root': {
			'& fieldset': { borderColor: '#FFF', color: '#FFF' },
			'&:hover fieldset': { borderColor: '#FFF', color: '#FFF', borderWidth: 2 },
			'&.Mui-focused fieldset': { borderColor: '#FFF', color: '#FFF' },
			'&.Mui-error fieldset': { borderColor: '#FFF' },
		},
	},
}));

const civitatisRepo = new CivitatisExperienceRepository();

const EntertainmentSearchInputs = () => {
	const [loading, setLoading] = useState(false);
	const history = useHistory();
	const classes = useStyles();

	const [inputValue, setInputValue] = useState('');
	const [showResults, setShowResults] = useState(false);
	const [activities, setActivities] = useState([]);
	const [destinations, setDestinations] = useState([]);
	const [searchId, setSearchId] = useState(0);
	const currentSearchId = useRef(0);

	useEffect(() => {
		const saved = getSessionStorage('entertainmentSearch');
		if (saved?.term) setInputValue(saved.term);
	}, []);

	const inputSearch = async (value) => {
		const newSearchId = searchId + 1;
		setSearchId(newSearchId);
		currentSearchId.current = newSearchId;
		setInputValue(value);
		setLoading(true);

		if (value.length >= 3) {
			try {
				const { data } = await civitatisRepo.autoComplete(value);
				if (currentSearchId.current !== newSearchId) return;

				// Server already filtered by term; trust the result and just strip free tours
				const filteredDestinations = data.destinations || [];
				const filteredActivities = filterFreeTours(data.activities || []);

				setDestinations(filteredDestinations);
				setActivities(filteredActivities);
				setShowResults(true);
			} catch (error) {
				if (currentSearchId.current !== newSearchId) return;
				console.error('Erro na busca:', error);
			} finally {
				if (currentSearchId.current === newSearchId) setLoading(false);
			}
		} else {
			setDestinations([]);
			setActivities([]);
			setShowResults(false);
			setLoading(false);
		}
	};

	const handleSelectDestination = async (destination) => {
		setLoading(true);
		try {
			const results = await civitatisRepo.search({ destinationId: destination.destinationId });
			cleanStorageBeforeSearch();
			sessionStorage.setItem(
				'entertainmentSearch',
				JSON.stringify({
					selectedDestination: destination,
					activities: filterFreeTours(results?.activities || []),
					destinations,
				})
			);
			sessionStorage.setItem('buscaAtual', 'Entretenimento');
			history.push('/busca/resultado-pesquisa');
		} catch (error) {
			console.error('Erro ao buscar destino:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectActivity = (activity) => {
		cleanStorageBeforeSearch();
		sessionStorage.setItem(
			'@EntertainmentSelected',
			JSON.stringify({ activityId: activity.activityId, refToken: activity.refToken, activity })
		);
		sessionStorage.setItem(
			'entertainmentSearch',
			JSON.stringify({
				activities: [activity],
				destinations: [],
				selectedActivity: activity,
			})
		);
		sessionStorage.setItem('buscaAtual', 'Entretenimento');
		history.push('/busca/entertainment-details');
	};

	// "Ver todos os resultados": o auto-complete ja trouxe a lista completa do termo
	// (a dropdown so exibe as 4 primeiras). Reaproveita o que esta em tela em vez de
	// refazer a busca no /search — que ignorava o termo e devolvia outro conjunto.
	const handleSearchSubmit = (term) => {
		if (!term || term.length < 2) return;
		cleanStorageBeforeSearch();
		sessionStorage.setItem(
			'entertainmentSearch',
			JSON.stringify({ term, activities, destinations })
		);
		sessionStorage.setItem('buscaAtual', 'Entretenimento');
		history.push('/busca/resultado-pesquisa');
	};

	return (
		<S.Wrapper>
			<Container>
				<div className='entretenimento-control'>
					<Grid container spacing={1}>
						<Grid>
							<S.Title>Onde deseja encontrar?</S.Title>
							<Grid item xs={12} sm={12} md={8} lg={6}>
								<Autocomplete
									freeSolo
									className={classes.root}
									noOptionsText='Não encontrado'
									options={[]}
									getOptionLabel={(option) => (option.name ? option.name : '')}
									inputValue={inputValue}
									onInputChange={(event, value, reason) => {
										if (reason === 'input') inputSearch(value);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder='Digite um destino'
											variant='outlined'
											fullWidth
											className={classes.root}
										/>
									)}
									disableClearable
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid className='results'>
						<SearchResults
							activities={activities}
							destinations={destinations}
							handleSelectDestination={handleSelectDestination}
							handleSelectActivity={handleSelectActivity}
							submit={handleSearchSubmit}
							searchTerm={inputValue}
							showResults={showResults}
							loading={loading}
						/>
					</Grid>
				</div>
			</Container>
		</S.Wrapper>
	);
};

export default EntertainmentSearchInputs;
